import type { PosterAssetDto, PosterTaskDto } from "../api/posterService";

/**
 * 详情页「形态」—— 2026-07-28 视觉舱精调 W2。
 *
 * 详情页要承载 5 种产物,老结构(元数据 → 主图+编辑器 → 底部网格)只对单图勉强够用:
 *   SINGLE     单张,居中按真实画幅撑高
 *   GROUP      爆点组图,每张讲一个卖点 → 主画面角标 + 卡底写爆点标题
 *   RATIO_SET  多比例套图,同内容多画幅 → 卡按各自比例渲染(对比才是意图)
 *   LONG_IMAGE 长图,容器定高内部滚动 + 分段面板
 *   WIDE       宽图,横向铺满(右列编辑面板会把 16:9 挤成一条缝)
 *
 * 判定放在纯函数里,页面只做渲染分支 —— 这几条判据以前散在 JSX 条件里各写各的。
 */
export type DetailShape = "SINGLE" | "GROUP" | "RATIO_SET" | "LONG_IMAGE" | "WIDE";

/** 宽图判据:横版且宽高比 ≥ 1.6(16:9=1.78、21:9=2.33 都进;4:3=1.33 不进)。 */
const WIDE_MIN_ASPECT = 1.6;

export function detectShape(
  task: Pick<PosterTaskDto, "posterKind" | "ratio" | "ratios" | "groupPointTitles"> | null,
  assets: Pick<PosterAssetDto, "actualWidth" | "actualHeight">[]
): DetailShape {
  if (!task) return "SINGLE";
  if (task.posterKind === "LONG_IMAGE") return "LONG_IMAGE";
  if (task.posterKind === "GROUP" || (task.groupPointTitles?.length ?? 0) > 0) return "GROUP";
  if (task.posterKind === "RATIO_SET" || (task.ratios?.length ?? 0) >= 2) return "RATIO_SET";

  // 宽图没有独立 posterKind(它就是单张 16:9),按画幅认:优先真实像素,回落请求比例。
  const first = assets.find((a) => a.actualWidth && a.actualHeight);
  if (first?.actualWidth && first.actualHeight) {
    if (first.actualWidth / first.actualHeight >= WIDE_MIN_ASPECT) return "WIDE";
  } else if (task.ratio) {
    const [w, h] = task.ratio.split(":").map(Number);
    if (Number.isFinite(w) && Number.isFinite(h) && h > 0 && w / h >= WIDE_MIN_ASPECT) {
      return "WIDE";
    }
  }
  return "SINGLE";
}

/**
 * 「本次设置」尺寸行文案。
 *
 * ★套图必须列全部比例 —— 老写法只读单值 `task.ratio`,一次出 9:16+1:1+16:9 的任务
 * 页面上写的是「9:16 · 1K」,用户看到的和拿到的对不上。
 */
export function sizeSummary(
  task: Pick<PosterTaskDto, "ratio" | "ratios" | "resolutionTier"> | null
): string {
  if (!task) return "-";
  const head =
    task.ratios && task.ratios.length >= 2 ? task.ratios.join(" + ") : (task.ratio || "");
  // 长图没有单一画幅(它是逐屏拼出来的)→ ratio 为空。此时只显示档位,
  // 不要在页头留一个孤零零的「-」(实测「GPT · - · 2K」读起来像缺数据)。
  if (!head) return task.resolutionTier ?? "-";
  return task.resolutionTier ? `${head} · ${task.resolutionTier}` : head;
}
